import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Shield, Mail, RefreshCw, Key,
  AlertTriangle, CheckCircle, Eye, EyeOff, Building2,
  Filter, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRoleBadgeColor = (role) => {
  const colors = {
    super_admin: 'bg-red-100 text-red-800',
    superadmin: 'bg-red-100 text-red-800',
    admin: 'bg-purple-100 text-purple-800',
    manager: 'bg-blue-100 text-blue-800',
    provider: 'bg-green-100 text-green-800',
    nurse: 'bg-teal-100 text-teal-800',
    receptionist: 'bg-orange-100 text-orange-800',
    staff: 'bg-gray-100 text-gray-800',
    patient: 'bg-cyan-100 text-cyan-800',
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
};

const UserPasswordManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetForm, setResetForm] = useState({
    manualPassword: '',
    useManualPassword: false,
    sendEmail: true
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(
        `${API_URL}/api/auth/admin/users?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, searchTerm]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchUsers]);

  const openResetModal = (user) => {
    setSelectedUser(user);
    setResetForm({
      manualPassword: '',
      useManualPassword: false,
      sendEmail: true
    });
    setShowPassword(false);
    setResetModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (resetForm.useManualPassword && resetForm.manualPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/admin/reset-password`,
        {
          user_id: selectedUser.id,
          new_password: resetForm.useManualPassword ? resetForm.manualPassword : null,
          send_email: resetForm.sendEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        if (response.data.is_temporary && response.data.temporary_password) {
          toast.success(
            <div>
              <p className="font-semibold">Password reset successfully!</p>
              {response.data.email_sent ? (
                <p className="text-sm">Email sent with new credentials</p>
              ) : (
                <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                  <p className="font-medium">Temporary Password:</p>
                  <code className="text-green-600 font-mono">{response.data.temporary_password}</code>
                  <p className="text-xs text-gray-500 mt-1">Copy and share securely with the user</p>
                </div>
              )}
            </div>,
            { duration: 15000 }
          );
        } else {
          toast.success('Password reset successfully!' + (response.data.email_sent ? ' Email sent.' : ''));
        }
        setResetModalOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="user-password-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg">
            <Key className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Password Management</h2>
            <p className="text-sm text-gray-500">Reset passwords and manage user credentials</p>
          </div>
        </div>
        <Button onClick={fetchUsers} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="user-search-input"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Clinic Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="provider">Provider</SelectItem>
                  <SelectItem value="nurse">Nurse</SelectItem>
                  <SelectItem value="receptionist">Receptionist</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="patient">Patient</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({users.length})</CardTitle>
          <CardDescription>Click on a user to reset their password</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="divide-y">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between py-4 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 -mx-4 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 font-semibold">
                        {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{user.full_name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      {user.clinic_name && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {user.clinic_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getRoleBadgeColor(user.role)}>
                      {user.role?.replace('_', ' ')}
                    </Badge>
                    {user.must_change_password && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Must Change
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openResetModal(user)}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      data-testid={`reset-password-btn-${user.id}`}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      Reset Password
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Modal */}
      <Dialog open={resetModalOpen} onOpenChange={setResetModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-600" />
              Reset User Password
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold">{selectedUser?.full_name}</span>
              <br />
              <span className="text-xs">{selectedUser?.email}</span>
              {selectedUser?.clinic_name && (
                <>
                  <br />
                  <span className="text-xs text-gray-400">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    {selectedUser.clinic_name}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Password Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Password Type</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <input
                    type="radio"
                    name="passwordType"
                    checked={!resetForm.useManualPassword}
                    onChange={() => setResetForm(prev => ({ ...prev, useManualPassword: false, manualPassword: '' }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <div>
                    <span className="text-sm font-medium">Auto-generate temporary password</span>
                    <p className="text-xs text-gray-500">User must change on next login</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <input
                    type="radio"
                    name="passwordType"
                    checked={resetForm.useManualPassword}
                    onChange={() => setResetForm(prev => ({ ...prev, useManualPassword: true }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <div>
                    <span className="text-sm font-medium">Set password manually</span>
                    <p className="text-xs text-gray-500">Define a specific password</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Manual Password Input */}
            {resetForm.useManualPassword && (
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password (min 8 characters)"
                    value={resetForm.manualPassword}
                    onChange={(e) => setResetForm(prev => ({ ...prev, manualPassword: e.target.value }))}
                    className="pr-10"
                    data-testid="manual-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {resetForm.manualPassword && resetForm.manualPassword.length < 8 && (
                  <p className="text-xs text-red-500">Password must be at least 8 characters</p>
                )}
              </div>
            )}

            {/* Send Email Option */}
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <input
                type="checkbox"
                id="sendEmail"
                checked={resetForm.sendEmail}
                onChange={(e) => setResetForm(prev => ({ ...prev, sendEmail: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="sendEmail" className="text-sm cursor-pointer flex-1">
                <Mail className="h-4 w-4 inline mr-1 text-blue-600" />
                Send email notification with new password
              </label>
            </div>

            {/* Warning */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              {resetForm.useManualPassword
                ? "The user will use this password to login. Make sure to share it securely."
                : "A secure temporary password will be generated. The user must change it on first login."
              }
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={submitting || (resetForm.useManualPassword && resetForm.manualPassword.length < 8)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-reset-password-btn"
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserPasswordManagement;
